import React, { useEffect, useMemo, useRef, useState } from "react";
import Form from "@rjsf/core";
import { customizeValidator } from "@rjsf/validator-ajv8";
import { react2angular } from "react18-react2angular";
import englishLocalizer from "ajv-i18n/localize/en";
import spanishLocalizer from "ajv-i18n/localize/es";
import _ from "lodash";
import { ENGLISH, SPANISH } from "../../../../app.constants";
import {
    getReactSchemaFormData,
    getSchemaFieldPathSegments,
} from "../../document_field_path.utils";

const DRAFT_07_SCHEMA = "http://json-schema.org/draft-07/schema#";
const AJV_LOCALIZERS = {
    [ENGLISH]: englishLocalizer,
    [SPANISH]: spanishLocalizer,
};
const validatorByLocale = {};

function normalizeValidatorLocale(locale) {
    if (!locale || typeof locale !== "string") {
        return ENGLISH;
    }

    let normalizedLocale = locale.toLowerCase().split("-")[0].split("_")[0];
    if (
        Object.prototype.hasOwnProperty.call(AJV_LOCALIZERS, normalizedLocale)
    ) {
        return normalizedLocale;
    }

    return ENGLISH;
}

function getDocumentLanguage() {
    if (typeof document === "undefined" || !document.documentElement) {
        return ENGLISH;
    }

    return document.documentElement.lang || ENGLISH;
}

function getValidatorForLocale(locale) {
    let normalizedLocale = normalizeValidatorLocale(locale);

    if (!validatorByLocale[normalizedLocale]) {
        validatorByLocale[normalizedLocale] = customizeValidator(
            {},
            AJV_LOCALIZERS[normalizedLocale],
        );
    }

    return validatorByLocale[normalizedLocale];
}

function forceAdditionalPropertiesFalse(schemaNode) {
    if (Array.isArray(schemaNode)) {
        return schemaNode.map(forceAdditionalPropertiesFalse);
    }

    if (!schemaNode || typeof schemaNode !== "object") {
        return schemaNode;
    }

    let normalizedNode = {};

    Object.keys(schemaNode).forEach(function (key) {
        normalizedNode[key] = forceAdditionalPropertiesFalse(schemaNode[key]);
    });

    if (
        Object.prototype.hasOwnProperty.call(
            normalizedNode,
            "additionalProperties",
        )
    ) {
        normalizedNode.additionalProperties = false;
    }

    return normalizedNode;
}

function getSchemaFingerprint(schema) {
    let serializedSchema = JSON.stringify(schema);
    let fingerprint = 2166136261;

    for (
        let characterIndex = 0;
        characterIndex < serializedSchema.length;
        characterIndex++
    ) {
        fingerprint ^= serializedSchema.charCodeAt(characterIndex);
        fingerprint = Math.imul(fingerprint, 16777619);
    }

    return (fingerprint >>> 0).toString(16);
}

function addSchemaFingerprintToId(schemaId, schema) {
    let fragmentIndex = schemaId.indexOf("#");
    let schemaIdWithoutFragment =
        fragmentIndex === -1 ? schemaId : schemaId.slice(0, fragmentIndex);
    let fragment = fragmentIndex === -1 ? "" : schemaId.slice(fragmentIndex);
    let parameterSeparator = schemaIdWithoutFragment.includes("?") ? "&" : "?";

    return (
        schemaIdWithoutFragment +
        parameterSeparator +
        "harvust_schema=" +
        getSchemaFingerprint(schema) +
        fragment
    );
}

function normalizeSchemaForAjv8(schema) {
    let normalizedSchema = schema
        ? { ...schema }
        : {
              type: "object",
              properties: {},
          };

    normalizedSchema.$schema = DRAFT_07_SCHEMA;

    if (normalizedSchema.id && !normalizedSchema.$id) {
        normalizedSchema.$id = normalizedSchema.id;
    }

    if (normalizedSchema.id) {
        delete normalizedSchema.id;
    }

    if (normalizedSchema.$id) {
        normalizedSchema.$id = addSchemaFingerprintToId(
            normalizedSchema.$id,
            normalizedSchema,
        );
    }

    return forceAdditionalPropertiesFalse(normalizedSchema);
}

function removeRootSchemaTitleAndDescription(schema) {
    if (!schema || typeof schema !== "object" || Array.isArray(schema)) {
        return schema;
    }

    let normalizedSchema = { ...schema };
    delete normalizedSchema.title;
    delete normalizedSchema.description;
    return normalizedSchema;
}

function sanitizeEventFormData(event, schema) {
    return getReactSchemaFormData((event && event.formData) || {}, schema);
}

function getEventErrors(event) {
    return (event && event.errors) || [];
}

function formatSlashDate(value) {
    let dateDigits = String(value || "")
        .replace(/\D/g, "")
        .slice(0, 8);

    if (dateDigits.length <= 2) {
        return dateDigits;
    }

    if (dateDigits.length <= 4) {
        return dateDigits.slice(0, 2) + "/" + dateDigits.slice(2);
    }

    return (
        dateDigits.slice(0, 2) +
        "/" +
        dateDigits.slice(2, 4) +
        "/" +
        dateDigits.slice(4)
    );
}

function SlashDateWidget(props) {
    let TextWidget = props.registry.widgets.TextWidget;

    function onChange(value) {
        props.onChange(formatSlashDate(value));
    }

    return React.createElement(TextWidget, {
        ...props,
        value: formatSlashDate(props.value),
        onChange,
    });
}

const widgets = {
    slashDate: SlashDateWidget,
};

function applyDateWidgets(uiSchema, schema, dateFields) {
    let resolvedUiSchema = _.cloneDeep(uiSchema || {});

    _.forEach(_.keys(dateFields || {}), function (fieldName) {
        let fieldPathSegments = getSchemaFieldPathSegments(schema, fieldName);
        if (fieldPathSegments.length === 0) {
            return;
        }

        _.set(
            resolvedUiSchema,
            _.concat(fieldPathSegments, "ui:widget"),
            "slashDate",
        );
    });

    return resolvedUiSchema;
}

function ReactSchemaForm(props) {
    let {
        schema: schemaProp,
        uiSchema,
        dateFields,
        formData: formDataProp,
        formId,
        className,
        disabled,
        readonly,
        showSubmit: showSubmitProp,
        submitLabel: submitLabelProp,
        submitButtonClassName: submitButtonClassNameProp,
        submitContainerClassName: submitContainerClassNameProp,
        validatorLocale: validatorLocaleProp,
        onChange: onChangeProp,
        onSubmit: onSubmitProp,
        onError: onErrorProp,
        onRendered,
        registerApi,
    } = props;

    let validatorLocale = normalizeValidatorLocale(
        validatorLocaleProp || getDocumentLanguage(),
    );
    let validator = getValidatorForLocale(validatorLocale);
    let schema = useMemo(
        function () {
            return removeRootSchemaTitleAndDescription(
                normalizeSchemaForAjv8(schemaProp),
            );
        },
        [schemaProp],
    );
    let formData = useMemo(
        function () {
            return getReactSchemaFormData(formDataProp || {}, schema);
        },
        [formDataProp, schema],
    );
    let resolvedUiSchema = useMemo(
        function () {
            return applyDateWidgets(uiSchema, schema, dateFields);
        },
        [uiSchema, schema, dateFields],
    );
    let showSubmit = showSubmitProp !== false;
    let submitLabel = _.unescape(submitLabelProp || "Submit");
    let submitButtonClassName =
        submitButtonClassNameProp || "btn-flat bg-harvustorange text-white";
    let submitContainerClassName = submitContainerClassNameProp || "mb-4 mt-8";
    let [currentFormData, setCurrentFormData] = useState(formData);
    let [submissionErrors, setSubmissionErrors] = useState(null);
    let formRef = useRef(null);

    function validateForm() {
        if (
            !formRef.current ||
            typeof formRef.current.validateForm !== "function"
        ) {
            return false;
        }

        return !!formRef.current.validateForm();
    }

    function submitForm() {
        if (!formRef.current) {
            return false;
        }

        if (typeof formRef.current.submit === "function") {
            formRef.current.submit();
            return true;
        }

        let formElement = _.get(formRef.current, "formElement");
        if (formElement && typeof formElement.requestSubmit === "function") {
            formElement.requestSubmit();
            return true;
        }

        return false;
    }

    useEffect(
        function () {
            setCurrentFormData(formData || {});
        },
        [formData],
    );

    useEffect(
        function () {
            if (typeof onRendered === "function") {
                onRendered();
            }
        },
        [schema, onRendered],
    );

    useEffect(
        function () {
            if (typeof registerApi !== "function") {
                return;
            }

            registerApi({
                validate: validateForm,
                submit: submitForm,
            });
        },
        [registerApi, schema, validatorLocale, formId],
    );

    function onChange(changeEvent) {
        let nextFormData = sanitizeEventFormData(changeEvent, schema);
        let nextErrors = getEventErrors(changeEvent);
        setCurrentFormData(nextFormData);
        if (submissionErrors !== null) {
            setSubmissionErrors(nextErrors);
        }
        if (typeof onChangeProp === "function") {
            onChangeProp({
                formData: nextFormData,
                errors: nextErrors,
            });
        }
    }

    function onSubmit(submitEvent) {
        let submittedFormData = sanitizeEventFormData(submitEvent, schema);
        setSubmissionErrors(null);
        if (typeof onSubmitProp === "function") {
            onSubmitProp({
                formData: submittedFormData,
            });
        }
    }

    function onError(errors) {
        let nextErrors = errors || [];
        setSubmissionErrors(nextErrors);
        if (typeof onErrorProp === "function") {
            onErrorProp({ errors: nextErrors });
        }
    }

    let errorSummary =
        submissionErrors && submissionErrors.length
            ? React.createElement(
                  "div",
                  { className: "mt-8" },
                  React.createElement(
                      "div",
                      {
                          className:
                              "rounded-xs border border-red-300 bg-red-50 p-3",
                      },
                      React.createElement(
                          "p",
                          {
                              className:
                                  "text-sm font-medium text-red-500 mb-1",
                          },
                          "Please correct the highlighted fields.",
                      ),
                      React.createElement(
                          "ul",
                          { className: "list-disc pl-5 text-sm text-red-500" },
                          submissionErrors.map(function (error, index) {
                              return React.createElement(
                                  "li",
                                  { key: "schema-error-" + index },
                                  error.stack || "Invalid field value",
                              );
                          }),
                      ),
                  ),
              )
            : null;

    let submitButton = showSubmit
        ? React.createElement(
              "div",
              { className: submitContainerClassName },
              React.createElement(
                  "button",
                  {
                      type: "submit",
                      className: submitButtonClassName,
                      disabled: !!disabled,
                  },
                  submitLabel,
              ),
          )
        : null;

    return React.createElement(
        Form,
        {
            ref: formRef,
            schema,
            uiSchema: resolvedUiSchema,
            widgets,
            formData: currentFormData,
            validator,
            id: formId,
            className,
            disabled,
            readonly,
            noHtml5Validate: true,
            showErrorList: false,
            focusOnFirstError: true,
            onChange,
            onSubmit,
            onError,
        },
        errorSummary,
        submitButton,
    );
}

const angularComponent = react2angular(ReactSchemaForm, [
    "schema",
    "uiSchema",
    "dateFields",
    "formData",
    "formId",
    "className",
    "disabled",
    "readonly",
    "showSubmit",
    "submitLabel",
    "submitButtonClassName",
    "submitContainerClassName",
    "validatorLocale",
    "onChange",
    "onSubmit",
    "onError",
    "onRendered",
    "registerApi",
]);

export default {
    ...angularComponent,
    name: "reactSchemaForm",
};
